import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { locale } from "../locale";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { userPlaceholder } from "../user/user";
import { centeredBox } from "../van/centered-box";
import {
	button,
	div,
	h1,
	input,
	label,
	option,
	p,
	select,
} from "../van/van-tags";

export class AccountPage implements Route {
	async #saveChanges(): Promise<void> {
		const oldLocale = this.#reactive.user.Locale;
		const req = this.#ctx.api.put(`/users/${this.#reactive.user.Id}`);
		req.setJsonBody({
			Email: this.#reactive.user.Email,
			Phone: this.#reactive.user.Phone,
			Locale: this.#reactive.user.Locale,
			EmailNotifications: {
				Billing: this.#reactive.user.EmailNotifications.Billing,
				Device: this.#reactive.user.EmailNotifications.Device,
				Marketing: this.#reactive.user.EmailNotifications.Marketing,
			},
			PushNotifications: this.#reactive.user.PushNotifications.Enabled,
			SmsNotifications: this.#reactive.user.SmsNotifications.Enabled,
		});
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			const user = await res.json();
			state.setUser(user);
			this.#reactive.user = user;
			if (user.Locale !== oldLocale) location.reload();
			this.#reactive.help.success = true;
			this.#reactive.help.text = locale.string({
				enGB: "Changes have been saved",
				deDE: "Änderungen wurden gespeichert",
				frFR: "Les modifications ont été enregistrées",
				svSE: "Ändringarna har sparats",
			});
		} else if (res.status === 400) {
			const err = await res.text();
			this.#reactive.help.success = false;
			this.#reactive.help.text = locale.string({
				enGB: `Save failed: ${err}`,
				deDE: `Speichern fehlgeschlagen: ${err}`,
				frFR: `Échec de l'enregistrement : ${err}`,
				svSE: `Spara misslyckades: ${err}`,
			});
		}
	}

	async refresh(_: Match): Promise<void> {
		this.#reactive.user = await state.getActiveUser();
	}

	#ctx;
	#reactive = reactive({
		help: {
			success: true,
			text: "",
		},
		user: userPlaceholder,
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1(
				{ class: "title" },
				locale.string({
					enGB: "Account settings",
					deDE: "Kontoeinstellungen",
					frFR: "Paramètres du compte",
					svSE: "Kontoinställningar",
				}),
			),
			div(
				{ class: "field" },
				label(
					{ class: "label" },
					locale.string({
						enGB: "Email",
						deDE: "E-Mail",
						frFR: "E-mail",
						svSE: "E-post",
					}),
				),
				div(
					{ class: "control" },
					input({
						class: "input",
						oninput: (e: Event) => {
							if (!(e.target instanceof HTMLInputElement)) return;
							this.#reactive.user.Email = e.target.value;
						},
						value: () => this.#reactive.user.Email,
					}),
				),
			),
			div(
				{ class: "field" },
				label(
					{ class: "label" },
					locale.string({
						enGB: "Phone",
						deDE: "Telefon",
						frFR: "Téléphone",
						svSE: "Telefon",
					}),
				),
				div(
					{ class: "control" },
					input({
						class: "input",
						oninput: (e: Event) => {
							if (!(e.target instanceof HTMLInputElement)) return;
							this.#reactive.user.Phone = e.target.value;
						},
						value: () => this.#reactive.user.Phone,
					}),
				),
			),
			div(
				{ class: "field" },
				label(
					{ class: "label" },
					locale.string({
						enGB: "Language",
						deDE: "Sprache",
						frFR: "Langue",
						svSE: "Språk",
					}),
				),
				div(
					{ class: "control" },
					div(
						{ class: "select" },
						select(
							{
								onchange: (e: Event) => {
									if (
										!(e.target instanceof HTMLSelectElement)
									)
										return;
									switch (e.target?.value) {
										case "de-DE":
										case "en-GB":
										case "fr-FR":
										case "sv-SE":
											break;
										default:
											return;
									}
									this.#reactive.user.Locale = e.target.value;
								},
							},
							[
								["de-DE", "Deutsch"],
								["en-GB", "English"],
								["fr-FR", "Français"],
								["sv-SE", "Svenska"],
							].map((lang) =>
								option(
									{
										selected: () =>
											this.#reactive.user.Locale ===
											lang[0],
										value: lang[0],
									},
									lang[1],
								),
							),
						),
					),
				),
			),
			div(
				{ class: "field" },
				label(
					{ class: "label" },
					locale.string({
						enGB: "Notifications",
						deDE: "Benachrichtigungen",
						frFR: "Notifications",
						svSE: "Aviseringar",
					}),
				),
				div(
					{ class: "control" },
					label(
						{ class: "checkbox" },
						input({
							checked: () =>
								this.#reactive.user.EmailNotifications.Device,
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.user.EmailNotifications.Device =
									e.target.checked;
							},
							type: "checkbox",
						}),
						locale.string({
							// deliberate initial space due to formatting
							enGB: " Email",
							deDE: " E-Mail",
							frFR: " E-mail",
							svSE: " E-post",
						}),
					),
				),
				div(
					{ class: "control" },
					label(
						{ class: "checkbox" },
						input({
							checked: () =>
								this.#reactive.user.PushNotifications.Enabled,
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.user.PushNotifications.Enabled =
									e.target.checked;
							},
							type: "checkbox",
						}),
						// deliberate initial space due to formatting
						" Push",
					),
				),
				div(
					{ class: "control" },
					label(
						{ class: "checkbox" },
						input({
							checked: () =>
								this.#reactive.user.SmsNotifications.Balance >
									0 &&
								this.#reactive.user.SmsNotifications.Enabled,
							disabled: () =>
								this.#reactive.user.SmsNotifications.Balance <
								1,
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.user.SmsNotifications.Enabled =
									e.target.checked;
							},
							type: "checkbox",
						}),
						// deliberate initial space due to formatting
						() =>
							` SMS - ${this.#reactive.user.SmsNotifications.Balance}`,
					),
				),
				div(
					{ class: "control" },
					button(
						{
							class: "button is-text",
							onclick: () => openNewsletterLink(),
						},
						locale.string({
							enGB: "Sign up for the newsletter",
							deDE: "Für den Newsletter anmelden",
							frFR: "S'inscrire à la newsletter",
							svSE: "Anmäl dig till nyhetsbrevet",
						}),
					),
				),
			),
			div(
				{ class: "field is-grouped is-grouped-multiline" },
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () => this.#saveChanges(),
						},
						locale.string({
							enGB: "Save",
							deDE: "Speichern",
							frFR: "Enregistrer",
							svSE: "Spara",
						}),
					),
				),
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () => {
								this.#ctx.router.jump("/account/buy-sms");
							},
						},
						locale.string({
							enGB: "Buy SMS",
							deDE: "SMS kaufen",
							frFR: "Acheter des SMS",
							svSE: "Köp SMS",
						}),
					),
				),
				div(
					{ class: "control" },
					button(
						{
							class: "button is-danger",
							onclick: () => {
								this.#ctx.router.jump("/account/delete");
							},
						},
						locale.string({
							enGB: "Delete account",
							deDE: "Konto löschen",
							frFR: "Supprimer le compte",
							svSE: "Radera konto",
						}),
					),
				),
			),
			p(
				{
					class: () =>
						`help ${this.#reactive.help.success ? "is-success" : "is-danger"}`,
				},
				() => this.#reactive.help.text,
			),
		);
	}
}

function openNewsletterLink(): void {
	const link = locale.string({
		enGB: "https://www.luda.farm/pages/newsletter",
		deDE: "https://www.luda.farm/de/pages/newsletter",
		frFR: "https://www.luda.farm/fr/pages/newsletter",
		svSE: "https://www.luda.farm/sv/pages/newsletter",
	});
	window.open(link, "_blank", "noopener,noreferrer");
}
